import * as THREE from 'three';

/** A pointer in reference space — a controller's target ray, typically. */
export type PointerRay = {
  origin: THREE.Vector3;
  /** Unit length. */
  direction: THREE.Vector3;
};

export type PanelHit = {
  /** 0..1 across the panel, left to right. */
  u: number;
  /** 0..1 down the panel, top to bottom (canvas convention). */
  v: number;
  /** Distance from the ray origin, metres. */
  distance: number;
  /** Hit position in world/reference space (for the pointer dot). */
  point: THREE.Vector3;
};

/** A clickable rectangle in normalised canvas space (0..1, origin top-left). */
export type PanelRegion = { id: string; x: number; y: number; w: number; h: number };

/** The ray that landed on a panel, so the caller can rest its pointer dot on it. */
export type PanelRayHit = { rayIndex: number; distance: number; point: THREE.Vector3 };

const localOrigin = new THREE.Vector3();
const localTarget = new THREE.Vector3();
const localDirection = new THREE.Vector3();
const inverse = new THREE.Matrix4();

/**
 * Intersect a pointer ray with a canvas panel and return its normalised canvas
 * coordinates, or undefined if the ray misses the quad.
 *
 * Shared by WristMenu and InfoPanel so both panels agree on hit behaviour, the
 * same way `SHOT_PRESETS` is shared by the keyboard and XR shot paths. Panels are
 * plane meshes centred on their own origin, facing +Z, so this is a plane
 * intersection plus a rectangle test.
 *
 * `matrixWorld` is refreshed here: panels are hit-tested during the animation
 * loop, before the renderer has updated the graph, and a wrist-worn panel moves
 * every frame.
 */
export function rayPanelUv(
  ray: PointerRay,
  panel: THREE.Object3D,
  width: number,
  height: number,
  maxDistance = 6,
): PanelHit | undefined {
  panel.updateMatrixWorld();
  inverse.copy(panel.matrixWorld).invert();

  localOrigin.copy(ray.origin).applyMatrix4(inverse);
  localTarget.copy(ray.origin).add(ray.direction).applyMatrix4(inverse);
  localDirection.subVectors(localTarget, localOrigin);

  // Parallel to the panel (or pointing away from it): no usable hit
  if (Math.abs(localDirection.z) < 1e-6) return undefined;
  const t = -localOrigin.z / localDirection.z;
  if (t <= 0 || t > maxDistance) return undefined;

  const x = localOrigin.x + localDirection.x * t;
  const y = localOrigin.y + localDirection.y * t;
  const u = x / width + 0.5;
  const v = 0.5 - y / height;
  if (u < 0 || u > 1 || v < 0 || v > 1) return undefined;

  return {
    u,
    v,
    distance: t,
    point: new THREE.Vector3(ray.origin.x, ray.origin.y, ray.origin.z)
      .addScaledVector(ray.direction, t),
  };
}

/**
 * The hover/press bookkeeping every ray-driven canvas panel needs: which region
 * the pointer is over, which one is lit, and the timing that stops one trigger
 * pull registering as several presses.
 *
 * `WristMenu` and `InfoPanel` each grew their own copy of this before it was
 * worth extracting; both still carry theirs. This exists so the next panel
 * doesn't become a third copy, and is deliberately additive — nothing is
 * migrated onto it until it has proven itself on something new.
 *
 * Deliberately NOT included: pose, drawing, and the canvas. Where a panel parks
 * and how it renders are the parts that actually differ between them.
 */
export class PanelSurface {
  regions: PanelRegion[] = [];
  hover?: string;
  pressed?: string;

  #elapsed = 0;
  #lastPressAt = -Infinity;
  #flashUntil = 0;
  #repress: number;
  #flash: number;

  constructor(options: { repressSeconds?: number; flashSeconds?: number } = {}) {
    this.#repress = options.repressSeconds ?? 0.3;
    this.#flash = options.flashSeconds ?? 0.18;
  }

  regionAt(u: number, v: number): PanelRegion | undefined {
    return this.regions.find(r => u >= r.x && u <= r.x + r.w && v >= r.y && v <= r.y + r.h);
  }

  /**
   * Point rays at the panel: advances the clock, expires a finished press flash
   * and updates `hover` to whatever the NEAREST ray is over. Returns that ray's
   * hit, or undefined if every ray missed.
   */
  aim(
    dt: number,
    rays: readonly PointerRay[],
    panel: THREE.Object3D,
    width: number,
    height: number,
  ): PanelRayHit | undefined {
    this.#elapsed += dt;

    let best: PanelRayHit | undefined;
    let hover: string | undefined;
    for (let i = 0; i < rays.length; i++) {
      const hit = rayPanelUv(rays[i], panel, width, height);
      if (!hit || (best && hit.distance >= best.distance)) continue;
      best = { rayIndex: i, distance: hit.distance, point: hit.point };
      hover = this.regionAt(hit.u, hit.v)?.id;
    }

    this.hover = hover;
    if (this.#elapsed >= this.#flashUntil) this.pressed = undefined;
    return best;
  }

  /**
   * Click whatever is hovered, returning its id — or undefined if nothing is
   * hovered or the last press was too recent to have been let go of.
   */
  press(): string | undefined {
    if (!this.hover) return undefined;
    if (this.#elapsed - this.#lastPressAt <= this.#repress) return undefined;
    this.#lastPressAt = this.#elapsed;
    this.pressed = this.hover;
    this.#flashUntil = this.#elapsed + this.#flash;
    return this.hover;
  }

  /** Seconds since construction — panels use it for their own idle timers. */
  get elapsed(): number {
    return this.#elapsed;
  }

  /** Drop transient state, keeping the clock (which only ever moves forward). */
  reset() {
    this.hover = undefined;
    this.pressed = undefined;
    this.#flashUntil = 0;
    this.#lastPressAt = -Infinity;
  }
}
